import { useState } from 'react';
import { useExportManagement } from '@/hooks/useExportManagement';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Download, FileText, Clock, CheckCircle, XCircle, Plus } from 'lucide-react';

export function DataExportManager() {
  const { exportRequests, exportStats, isLoading, createExport, downloadExport, isCreating } = useExportManagement();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [exportType, setExportType] = useState('bills');
  const [filters, setFilters] = useState('{}');

  const statusColors = {
    pending: 'default',
    processing: 'default',
    completed: 'default',
    failed: 'destructive'
  } as const;

  const statusIcons = {
    pending: Clock,
    processing: Clock,
    completed: CheckCircle,
    failed: XCircle
  };

  const handleCreateExport = () => {
    try {
      const parsedFilters = JSON.parse(filters);
      createExport({
        export_type: exportType,
        filters: parsedFilters
      });
      setShowCreateDialog(false);
      setFilters('{}');
    } catch (error) {
      // Invalid JSON in filters
      alert('Please enter valid JSON for filters');
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 bg-muted animate-pulse rounded w-20"></div>
                <div className="h-4 w-4 bg-muted animate-pulse rounded"></div>
              </CardHeader>
              <CardContent>
                <div className="h-7 bg-muted animate-pulse rounded w-8 mb-1"></div>
                <div className="h-3 bg-muted animate-pulse rounded w-16"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Data Export Manager</h2>
          <p className="text-muted-foreground">Export your data in various formats with advanced filtering</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Export
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Data Export</DialogTitle>
              <DialogDescription>
                Configure your data export with specific filters and parameters.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="exportType">Export Type</Label>
                <Select value={exportType} onValueChange={setExportType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bills">Bills Export</SelectItem>
                    <SelectItem value="suppliers">Suppliers Export</SelectItem>
                    <SelectItem value="customers">Customers Export</SelectItem>
                    <SelectItem value="analytics">Analytics Export</SelectItem>
                    <SelectItem value="audit_logs">Audit Logs Export</SelectItem>
                    <SelectItem value="processing_logs">Processing Logs Export</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="filters">Filters (JSON)</Label>
                <Textarea
                  id="filters"
                  value={filters}
                  onChange={(e) => setFilters(e.target.value)}
                  placeholder='{"date_range": {"start": "2024-01-01", "end": "2024-12-31"}}'
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  Enter JSON filters to customize your export. Leave empty for all data.
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateExport} disabled={isCreating}>
                {isCreating ? "Creating..." : "Create Export"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Export Statistics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Exports</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{exportStats.total}</div>
            <p className="text-xs text-muted-foreground">all time</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{exportStats.completed}</div>
            <p className="text-xs text-muted-foreground">successfully exported</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{exportStats.pending}</div>
            <p className="text-xs text-muted-foreground">in queue</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Size</CardTitle>
            <Download className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatFileSize(exportStats.totalSize)}</div>
            <p className="text-xs text-muted-foreground">total data exported</p>
          </CardContent>
        </Card>
      </div>

      {/* Export Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle>Export History</CardTitle>
          <CardDescription>
            View and manage all your data export requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {exportRequests.map((request) => {
                const StatusIcon = statusIcons[request.status as keyof typeof statusIcons];
                return (
                  <TableRow key={request.id}>
                    <TableCell className="font-medium">
                      {request.export_type.replace('_', ' ')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <StatusIcon className="h-4 w-4" />
                        <Badge variant={statusColors[request.status as keyof typeof statusColors]}>
                          {request.status}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="w-24">
                        <Progress value={request.progress} className="h-2" />
                        <span className="text-xs text-muted-foreground">{request.progress}%</span>
                      </div>
                    </TableCell>
                    <TableCell>{formatFileSize(request.file_size)}</TableCell>
                    <TableCell>
                      {new Date(request.requested_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {new Date(request.expires_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {request.status === 'completed' && request.file_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadExport(request)}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Download
                        </Button>
                      )}
                      {request.status === 'failed' && (
                        <Badge variant="destructive" className="text-xs">
                          {request.error_message || 'Failed'}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {exportRequests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No export requests found. Create your first export to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Export Guidelines */}
      <Card>
        <CardHeader>
          <CardTitle>Export Guidelines</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold mb-2">Supported Export Types</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Bills Export - All bill data with processing details</li>
                <li>• Suppliers Export - Supplier information and performance</li>
                <li>• Customers Export - Customer data and relationships</li>
                <li>• Analytics Export - Analytics events and metrics</li>
                <li>• Audit Logs Export - Security and activity logs</li>
                <li>• Processing Logs Export - Bill processing history</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Filter Examples</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Date range: {`{"date_range": {"start": "2024-01-01"}}`}</li>
                <li>• Status filter: {`{"status": "completed"}`}</li>
                <li>• Amount range: {`{"amount": {"min": 100, "max": 1000}}`}</li>
                <li>• Supplier ID: {`{"supplier_id": "uuid-here"}`}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}